import React from 'react';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';

export interface TripOptionCardProps {
	selected: boolean;
	onClick: () => void;
	title: React.ReactNode;
	/** One line per sailing the option covers, each shown with a boat icon. */
	routes: string[];
	/** Shown instead of the routes when there are none to show. */
	fallback?: React.ReactNode;
	testId?: string;
}

/**
 * A radio-style card for choosing which trips something applies to. Shared by the booking detailed
 * view's `Edit booking` wizard and the cart's `Edit` / `Remove` on the fare-type summary, so the two
 * questions look the same wherever a booker meets them.
 */
export default function TripOptionCard({ selected, onClick, title, routes, fallback, testId }: TripOptionCardProps) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-pressed={selected}
			data-test-id={testId}
			className={`w-full text-left p-4 border rounded-xl transition-all cursor-pointer ${
				selected
					? 'border-sky-500 bg-sky-50 ring-1 ring-sky-500'
					: 'border-slate-200 hover:border-slate-300 bg-white'
			}`}
		>
			<div className="flex items-center gap-4">
				<span
					className={`w-6 h-6 rounded-full border flex items-center justify-center transition-colors shrink-0 ${
						selected ? 'bg-sky-500 border-sky-500' : 'bg-white border-slate-300'
					}`}
				>
					{selected && <span className="w-2.5 h-2.5 rounded-full bg-white" />}
				</span>
				<div className="flex flex-col min-w-0">
					<span className="font-semibold text-slate-800">{title}</span>
					{routes.length > 0
						? routes.map(route => (
								<span key={route} className="text-[12px] text-slate-500 mt-0.5 flex items-center gap-1">
									<Icon
										name="directions_boat"
										useMaterialIcons
										classname="!text-[14px] text-slate-400"
									/>
									{route}
								</span>
							))
						: fallback && <span className="text-[12px] text-slate-500 mt-0.5">{fallback}</span>}
				</div>
			</div>
		</button>
	);
}
